import React, { useCallback, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import styled, { css } from "styled-components";
import { useImmerReducer } from "use-immer";

import { GraphQLAPI, GraphQLResult, parseItem, queries } from "@examspark/api";
import { FeedbackButton } from "@examspark/common-app";
import {
  AssetTitle,
  Button,
  FullFlexHorizontal,
  SwitchButton,
} from "@examspark/common-ui";
import {
  IconEraserLine,
  IconMagicWandLine,
  IconPencilLine,
  IconSuccessCircleLine,
} from "@examspark/common-ui/dist/components/icons";
import { filterMaybeArray } from "@examspark/common-util";
import Question, { QuestionContext, reducer } from "@examspark/question-player";
import { scoreItem } from "@examspark/score";
import {
  ExamBlobs,
  ExamSessionMetadata,
  ParsedItem,
  Question as QuestionType,
  Resource,
  Subject,
} from "@examspark/types";

const Toolbar = styled.div`
  color: #555;
  /* position: relative; */
  display: flex;
  align-items: center;
  border-bottom: solid 1px #e2e2e2;
  background-color: #f7f7f7;
  padding: 0.7rem 1rem;
  /* margin-bottom: 1rem; */
  min-width: 0;

  button {
    margin-right: 0;
    margin-left: 0;
  }
`;

const ToolbarScore = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Styled = styled.div`
  display: flex;
  height: 100%;
  box-sizing: border-box;
`;

interface ContentProps {
  padded?: boolean;
  isLocked?: boolean;
}
const Content = styled.div<ContentProps>`
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: auto;

  ${(props) => {
    if (props.padded) {
      return `
        margin: 1rem;
      `;
    }
  }}

  ${(props) => {
    if (props.isLocked) {
      return css`
        & .item-player-pad {
          position: relative;

          &::after {
            content: "";
            display: block;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;

            cursor: not-allowed;
          }
        }
      `;
    } else {
      return "";
    }
  }}
`;

const Wrapper = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
`;

const Score = styled.span`
  background-color: #fff;
  border: solid 1px #ddd;
  border-radius: 0.2rem;
  display: inline-block;
  line-height: 2.6rem;
  padding: 0 1rem;
  font-weight: normal;
  width: 9rem;
  text-align: center;
  margin-left: 1rem;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

type ItemPreviewProps =
  // common props on all calls
  {
    condensed?: boolean;
    response?: any;
    blobs?: ExamBlobs[string];
    scoreStudentResponse?: boolean;
    sessionData?: ExamSessionMetadata;
    isLocked?: boolean;
    onRequestEdit?: (data: ParsedItem) => void;
  } & ( // You may either specify the full item data, or just the item ID
    | {
        data: ParsedItem | undefined;
        itemId?: undefined;
      }
    | {
        data?: undefined;
        itemId: string | undefined;
      }
  ) &
    // if you hide the toolbar, you are cannot ask for customizations of said toolbar :p
    (| {
          hideToolbar: true;
          showKey?: boolean;
          showAutoRevealKey?: undefined;
        }
      | {
          hideToolbar?: false;
          showAutoRevealKey?: boolean;
          showKey?: undefined;
          toolbar?: React.ReactNode;
        }
    );

/**
 * Loads and displays preview of an item.
 *
 * Note: Similar to `ItemBrowserPreview` except it is not rendered in
 * `condensed` mode. This component also allows you to pass it item data vs
 * fetching.
 *
 * @todo Would be a good candiate for consolidation sometime.
 */

const ItemPreview: React.FC<React.PropsWithChildren<ItemPreviewProps>> = (
  props,
) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    response: {},
  });
  const [score, setScore] = useState<any>();
  const [data, setData] = useState<ParsedItem>();
  const [hasLoadError, setHasLoadError] = useState(false);
  const [autoRevealKey, setAutoRevealKey] = useState(false);

  const showKey = props.showKey ?? autoRevealKey;
  const itemData = props.data ?? data;

  const fetchItem = useCallback(async () => {
    if (!props.itemId) return;
    dispatch({
      type: "RESET",
    });
    const result = (await GraphQLAPI.query({
      query: queries.item.getItemForPreview,
      variables: {
        id: props.itemId,
        sk: "item#v0",
      },
    })) as GraphQLResult<{
      getResource: Resource;
    }>;

    if (!result.data?.getResource) {
      setHasLoadError(true);
      return;
    }
    setHasLoadError(false);
    setData(parseItem(result.data.getResource));
  }, [dispatch, props.itemId]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem, props.itemId]);

  // Init when the question data or shuffle seed changes
  const shuffleOptions = props.sessionData?.shuffleOptions;
  useEffect(() => {
    if (itemData) {
      dispatch({
        type: "INIT",
        data: {
          question: itemData as QuestionType,
          shuffleSeed: shuffleOptions
            ? (props.sessionData?.shuffleSeed ?? undefined)
            : undefined,
        },
      });
      return;
    }
  }, [dispatch, itemData, props.sessionData?.shuffleSeed, shuffleOptions]);

  useEffect(() => {
    if (showKey) {
      dispatch({
        type: "REVEAL_KEY",
      });
    }
  }, [showKey, data?.id, dispatch]);

  // If the response is passed in, score it
  // unless the key is shown, in which case scoring the response
  // would override the revealed key
  useEffect(() => {
    if (props.response && !showKey) {
      dispatch({
        type: "RESPONSE",
        data: {
          score: props.scoreStudentResponse,
          id: props.response.id,
          response: props.response,
          viewOnly: true,
          blobs: props.blobs,
        },
      });
    }
    if (showKey) {
      dispatch({
        type: "REVEAL_KEY",
        data: {
          response: props.response,
        },
      });
    }
  }, [
    dispatch,
    props.blobs,
    props.response,
    props.scoreStudentResponse,
    showKey,
  ]);

  useEffect(() => {
    // console.log("player state", state);
    if (!state) return;
    const scorableState = state;

    if (!scorableState.question) return;
    const sc = scoreItem(scorableState.question, scorableState.response);
    setScore(sc);
  }, [state]);

  useHotkeys(
    "r",
    () => {
      if (!props.showAutoRevealKey) return;
      return setAutoRevealKey(!autoRevealKey);
    },
    [autoRevealKey],
  );
  useHotkeys(
    "alt+r",
    () => {
      fetchItem();
    },
    [fetchItem],
  );

  const itemSubject = filterMaybeArray(data?.subject)[0] as Subject | undefined;
  const itemId = data?.id ?? props.itemId;

  return (
    <QuestionContext.Provider value={[state, dispatch]}>
      <Styled>
        <Main>
          {!props.hideToolbar && (
            <Toolbar>
              {!!state.question && (
                <FullFlexHorizontal>
                  <AssetTitle item={state.question as Resource} />
                </FullFlexHorizontal>
              )}
              {props.toolbar}
              <ToolbarScore>
                {props.showAutoRevealKey && (
                  <SwitchButton
                    disabled={false}
                    label="Auto-Reveal Key"
                    onClick={() => {
                      if (!autoRevealKey) {
                        dispatch({
                          type: "REVEAL_KEY",
                        });
                      }
                      setAutoRevealKey(!autoRevealKey);
                    }}
                    checked={autoRevealKey}
                  />
                )}
                {props.onRequestEdit && (
                  <Button
                    data-cy="item-preview-edit"
                    variant="plain"
                    title="Open Editor"
                    onClick={() => {
                      if (!data) return;
                      props.onRequestEdit?.(data);
                    }}
                  >
                    <IconPencilLine width="16" />
                    {!props.condensed && <>Edit</>}
                  </Button>
                )}
                <FeedbackButton
                  data-cy="item-preview-feedback"
                  variant="plain"
                  title="Feedback"
                  condensed={props.condensed}
                  showItemFeedbackHint={false}
                  itemSubject={itemSubject}
                  itemId={itemId}
                />
                <Button
                  data-cy="item-preview-check-answer"
                  variant="plain"
                  title="Check Answer"
                  onClick={() => {
                    dispatch({
                      type: "SCORE_RESPONSE",
                    });
                  }}
                >
                  <IconSuccessCircleLine width="16" />
                  {!props.condensed && <>Check Answer</>}
                </Button>
                <Button
                  data-cy="item-preview-reveal-answer"
                  variant="plain"
                  title="Reveal Key"
                  onClick={() => {
                    dispatch({
                      type: "REVEAL_KEY",
                    });
                  }}
                >
                  <IconMagicWandLine width="16" />
                  {!props.condensed && <>Reveal Key</>}
                </Button>
                <Button
                  data-cy="item-preview-reset"
                  title="Reset"
                  variant="plain"
                  onClick={() => {
                    dispatch({
                      type: "RESET",
                    });
                  }}
                >
                  <IconEraserLine width="16" />
                  {!props.condensed && <>Reset</>}
                </Button>
                {score && (
                  <Score data-cy="item-preview-score">
                    {`Score: ${score?.actual ?? "?"} / ${score?.max}`}
                  </Score>
                )}
              </ToolbarScore>
            </Toolbar>
          )}

          <Content padded={props.condensed} isLocked={props.isLocked}>
            {hasLoadError && <div>Could not load item.</div>}
            {!hasLoadError && (
              <Wrapper>
                <Question
                  condensed={props.condensed}
                  key={props.data?.id ?? data?.id}
                />
              </Wrapper>
            )}
          </Content>
        </Main>
      </Styled>
    </QuestionContext.Provider>
  );
};
export default ItemPreview;
